// OverviewTab — Premium Admin Dashboard with Gauges & Rich Widgets
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Users, BookOpen, FileText, Video, FolderOpen,
  TrendingUp, TrendingDown, Activity, Clock, UserPlus,
  CheckCircle, AlertCircle, Eye, Layers, FlaskConical,
  CalendarClock, HardDrive, BarChart3, Loader2
} from "lucide-react";

const getInitials = (f, l) => `${(f||"")[0]||""}${(l||"")[0]||""}`.toUpperCase();

// Animated counter hook
function useAnimatedCounter(target, duration = 800) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!target) { setValue(0); return; }
    let start = 0;
    const step = (ts) => {
      if (!ref.current) ref.current = ts;
      const progress = Math.min((ts - ref.current) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    ref.current = null;
    requestAnimationFrame(step);
  }, [target, duration]);
  return value;
}

// SVG Donut Chart
function DonutChart({ data, size = 140, strokeWidth = 18 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let offset = 0;

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.map((d, i) => {
          const pct = d.value / total;
          const dashLen = pct * circumference;
          const dashOffset = -offset * circumference;
          offset += pct;
          return (
            <circle
              key={i}
              cx={size / 2} cy={size / 2} r={radius}
              fill="none" stroke={d.color} strokeWidth={strokeWidth}
              strokeDasharray={`${dashLen} ${circumference - dashLen}`}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
              style={{ transition: "all 0.8s ease", transform: "rotate(-90deg)", transformOrigin: "center" }}
            />
          );
        })}
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center"
      }}>
        <span style={{ fontSize: "1.6rem", fontWeight: 700, color: "#1f2937" }}>{total}</span>
        <span style={{ fontSize: "0.65rem", color: "#6b7280", fontWeight: 500 }}>Total</span>
      </div>
    </div>
  );
}

// Mini Sparkline
function Sparkline({ data, color = "#7c3aed", height = 40, width = 120 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (d.count / max) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg width={width} height={height} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id={`sparkGrad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" points={points} />
      <polygon
        fill={`url(#sparkGrad-${color.replace('#','')})`}
        points={`0,${height} ${points} ${width},${height}`}
      />
    </svg>
  );
}

// Gauge component
function GaugeWidget({ value, max, label, color = "#7c3aed", icon: Icon }) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const angle = pct * 180;

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ position: "relative", width: 100, height: 55, margin: "0 auto" }}>
        <svg width="100" height="55" viewBox="0 0 100 55">
          <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="#e5e7eb" strokeWidth="8" strokeLinecap="round" />
          <path
            d="M 10 50 A 40 40 0 0 1 90 50"
            fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${angle * 1.396} 999`}
            style={{ transition: "stroke-dasharray 1s ease" }}
          />
        </svg>
        <div style={{
          position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
          fontSize: "1.1rem", fontWeight: 700, color: "#1f2937"
        }}>
          {value}
        </div>
      </div>
      <p style={{ fontSize: "0.7rem", color: "#6b7280", marginTop: 4, fontWeight: 500 }}>{label}</p>
    </div>
  );
}

// Stat Card
function StatCard({ icon: Icon, label, value, subtitle, color, trend }) {
  const animatedValue = useAnimatedCounter(value);
  return (
    <div style={{
      background: "#fff", borderRadius: 16, padding: "1.25rem 1.5rem",
      border: "1px solid #f0f0f0", position: "relative", overflow: "hidden",
      transition: "all 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 25px rgba(124,58,237,0.08)"; e.currentTarget.style.borderColor = `${color}30`; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; e.currentTarget.style.borderColor = "#f0f0f0"; }}
    >
      <div style={{
        position: "absolute", top: -20, right: -20, width: 80, height: 80,
        borderRadius: "50%", background: `${color}08`
      }} />
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</p>
          <p style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1f2937", marginTop: 4 }}>{animatedValue}</p>
          {subtitle && <p style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: 2 }}>{subtitle}</p>}
        </div>
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: `linear-gradient(135deg, ${color}15, ${color}08)`,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <Icon style={{ width: 20, height: 20, color }} />
        </div>
      </div>
      {trend !== undefined && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8,
          fontSize: "0.7rem", fontWeight: 600, padding: "2px 8px", borderRadius: 999,
          background: trend >= 0 ? "#ecfdf5" : "#fef2f2",
          color: trend >= 0 ? "#059669" : "#dc2626"
        }}>
          {trend >= 0 ? <TrendingUp style={{ width: 12, height: 12 }} /> : <TrendingDown style={{ width: 12, height: 12 }} />}
          {trend >= 0 ? "+" : ""}{trend} cette semaine
        </div>
      )}
    </div>
  );
}

const roleColors = {
  admin: "#7c3aed", trainer: "#3b82f6", manager: "#f59e0b",
  candidate: "#10b981", student: "#10b981"
};
const roleLabels = {
  admin: "Admins", trainer: "Formateurs", manager: "Gestionnaires",
  candidate: "Candidats", student: "Étudiants"
};

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "à l'instant";
    if (mins < 60) return `il y a ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `il y a ${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `il y a ${days}j`;
  } catch { return ""; }
}

const activityColors = {
  create: "#10b981", update: "#3b82f6", delete: "#ef4444",
  login: "#7c3aed", register: "#f59e0b", approve: "#10b981",
  update_permissions: "#f59e0b"
};

export default function OverviewTab({ stats, activities, loading }) {
  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem" }}>
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats) return null;

  const rolesData = Object.entries(stats.users_by_role || {}).map(([role, count]) => ({
    label: roleLabels[role] || role,
    value: count,
    color: roleColors[role] || "#6b7280"
  }));

  const gradingPct = stats.total_submissions > 0
    ? Math.round((stats.graded_submissions / stats.total_submissions) * 100) : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Row 1: Main Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem" }}>
        <StatCard icon={Users} label="Utilisateurs" value={stats.total_users} color="#7c3aed"
          subtitle={`${stats.active_users_7d || 0} actifs cette semaine`} trend={stats.new_users_7d} />
        <StatCard icon={FolderOpen} label="Workspaces" value={stats.total_workspaces} color="#3b82f6" />
        <StatCard icon={BookOpen} label="Cours" value={stats.total_courses} color="#10b981" />
        <StatCard icon={FileText} label="Devoirs" value={stats.total_assignments} color="#f59e0b" />
        <StatCard icon={FlaskConical} label="TPs & Quiz" value={(stats.total_tps || 0) + (stats.total_quizzes || 0)} color="#ec4899"
          subtitle={`${stats.total_tps || 0} TPs • ${stats.total_quizzes || 0} Quiz`} />
        <StatCard icon={Video} label="Réunions" value={stats.total_meetings} color="#6366f1" />
        <StatCard icon={HardDrive} label="Fichiers" value={stats.total_files} color="#0ea5e9" />
        <StatCard icon={CalendarClock} label="Sessions Temp." value={stats.temp_sessions_total} color="#8b5cf6"
          subtitle={`${stats.temp_sessions_active || 0} actives • ${stats.temp_session_visits || 0} visites`} />
      </div>

      {/* Row 2: Charts & Gauges */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem" }}>
        {/* Donut: Users by role */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" /> Répartition des rôles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1.5rem" }}>
              <DonutChart data={rolesData} />
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {rolesData.map(d => (
                  <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem" }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                    <span style={{ color: "#6b7280" }}>{d.label}</span>
                    <span style={{ fontWeight: 700, color: "#1f2937", marginLeft: "auto" }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gauges */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" /> Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: "flex", justifyContent: "space-around", paddingTop: 8 }}>
              <GaugeWidget value={stats.active_users_7d || 0} max={stats.total_users || 1} label="Utilisateurs actifs" color="#7c3aed" />
              <GaugeWidget value={gradingPct} max={100} label={`Corrigés (${gradingPct}%)`} color="#10b981" />
              <GaugeWidget value={stats.pending_users || 0} max={Math.max(stats.pending_users || 0, 5)} label="En attente" color="#f59e0b" />
            </div>
          </CardContent>
        </Card>

        {/* Sparkline: Registrations trend */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" /> Inscriptions (14 jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, paddingTop: 8 }}>
              <Sparkline data={stats.registration_trend || []} width={260} height={60} color="#7c3aed" />
              <div style={{ display: "flex", gap: "1.5rem", marginTop: 4 }}>
                <div style={{ textAlign: "center" }}>
                  <p style={{ fontSize: "1.2rem", fontWeight: 700, color: "#1f2937" }}>{stats.new_users_7d || 0}</p>
                  <p style={{ fontSize: "0.65rem", color: "#9ca3af" }}>7 derniers jours</p>
                </div>
                <div style={{ textAlign: "center" }}>
                  <p style={{ fontSize: "1.2rem", fontWeight: 700, color: "#1f2937" }}>{stats.new_users_30d || 0}</p>
                  <p style={{ fontSize: "0.65rem", color: "#9ca3af" }}>30 derniers jours</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Submissions & Recent users + Activity */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
        {/* Submissions overview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Layers className="h-4 w-4 text-primary" /> Soumissions
            </CardTitle>
            <CardDescription className="text-xs">{stats.total_submissions || 0} au total</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Graded bar */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", marginBottom: 4 }}>
                  <span style={{ color: "#6b7280" }}>Corrigées</span>
                  <span style={{ fontWeight: 600, color: "#10b981" }}>{stats.graded_submissions || 0}</span>
                </div>
                <div style={{ height: 8, background: "#f3f4f6", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 999, transition: "width 1s ease",
                    background: "linear-gradient(90deg, #10b981, #34d399)",
                    width: `${gradingPct}%`
                  }} />
                </div>
              </div>
              {/* Ungraded bar */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", marginBottom: 4 }}>
                  <span style={{ color: "#6b7280" }}>En attente</span>
                  <span style={{ fontWeight: 600, color: "#f59e0b" }}>{stats.ungraded_submissions || 0}</span>
                </div>
                <div style={{ height: 8, background: "#f3f4f6", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 999, transition: "width 1s ease",
                    background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
                    width: `${100 - gradingPct}%`
                  }} />
                </div>
              </div>
              {/* Weekly stats */}
              <div style={{ display: "flex", gap: "1rem", marginTop: 8, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
                <div style={{ flex: 1, textAlign: "center", padding: "0.5rem", background: "#f5f3ff", borderRadius: 10 }}>
                  <p style={{ fontSize: "1.1rem", fontWeight: 700, color: "#7c3aed" }}>{stats.submissions_7d || 0}</p>
                  <p style={{ fontSize: "0.65rem", color: "#9ca3af" }}>Cette semaine</p>
                </div>
                <div style={{ flex: 1, textAlign: "center", padding: "0.5rem", background: "#f0fdf4", borderRadius: 10 }}>
                  <p style={{ fontSize: "1.1rem", fontWeight: 700, color: "#10b981" }}>{stats.submissions_30d || 0}</p>
                  <p style={{ fontSize: "0.65rem", color: "#9ca3af" }}>Ce mois</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent users */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-primary" /> Dernières inscriptions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(stats.recent_users || []).map((u, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "0.4rem 0.5rem",
                  borderRadius: 10, transition: "background 0.15s"
                }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-[10px] font-bold">
                      {getInitials(u.first_name, u.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: "0.8rem", fontWeight: 600, color: "#1f2937" }}>
                      {u.first_name} {u.last_name}
                    </p>
                    <p style={{ fontSize: "0.7rem", color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.email}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize" style={{
                    borderColor: roleColors[u.role] + "40",
                    color: roleColors[u.role],
                    background: roleColors[u.role] + "08"
                  }}>
                    {roleLabels[u.role] || u.role}
                  </Badge>
                  <span style={{ fontSize: "0.65rem", color: "#9ca3af", whiteSpace: "nowrap" }}>
                    {formatRelativeTime(u.created_at)}
                  </span>
                </div>
              ))}
              {(!stats.recent_users || stats.recent_users.length === 0) && (
                <p style={{ textAlign: "center", color: "#9ca3af", fontSize: "0.8rem", padding: "1rem" }}>Aucune inscription récente</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Activity Timeline */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" /> Activité récente
          </CardTitle>
          <CardDescription className="text-xs">Les dernières actions sur la plateforme</CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ display: "flex", flexDirection: "column", gap: 0, position: "relative" }}>
            {/* Vertical line */}
            <div style={{
              position: "absolute", left: 15, top: 12, bottom: 12, width: 2,
              background: "linear-gradient(to bottom, #e5e7eb, transparent)"
            }} />
            {(activities || stats.recent_activities || []).slice(0, 12).map((a, i) => {
              const color = activityColors[a.action] || "#6b7280";
              return (
                <div key={a.id || i} style={{
                  display: "flex", alignItems: "flex-start", gap: 12,
                  padding: "0.5rem 0", position: "relative"
                }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%", background: color,
                    marginTop: 5, flexShrink: 0, border: "2px solid #fff",
                    boxShadow: `0 0 0 2px ${color}30`, zIndex: 1, marginLeft: 10
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: "0.8rem", lineHeight: 1.4 }}>
                      <span style={{ fontWeight: 600, color: "#1f2937" }}>{a.user_name}</span>
                      {" "}
                      <span style={{ color: "#6b7280" }}>{a.action} {a.resource_type}</span>
                      {a.details && (
                        <span style={{ color: "#9ca3af", fontSize: "0.75rem" }}> — {
                          a.details.length > 80 ? a.details.substring(0, 80) + "…" : a.details
                        }</span>
                      )}
                    </p>
                  </div>
                  <span style={{ fontSize: "0.65rem", color: "#9ca3af", whiteSpace: "nowrap", marginTop: 2 }}>
                    {formatRelativeTime(a.created_at)}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
